import { TileNodeProps } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { useEntityDiagramContext } from '@/modules/entityDiagram/contexts/entityDiagram.context';
import { EntityDiagramGraph } from '@/modules/entityDiagram/types';
import {
  EntityTileTarget,
  PresentationEditableEntityTile,
} from '@/modules/presentation/clientPresentation/components/presentationEntityActions/PresentationEditableEntityTile';

/**
 * Resolve the editable record behind an entity-diagram tile from the graph
 * (keyed by the React Flow node id), or null when the node has no edit
 * affordance (placeholder/pixel tiles).
 */
function resolveNodeTarget(
  graph: EntityDiagramGraph | null,
  nodeId: string
): EntityTileTarget | null {
  if (!graph || !graph.hasNode(nodeId)) {
    return null;
  }

  const { node } = graph.getNodeAttributes(nodeId).data;
  // Placeholder/pixel tiles carry no entity node; skip them.
  if (!node) {
    return null;
  }

  switch (node.__typename) {
    case 'Entity':
      return {
        kind: 'entity',
        entityId: node.id,
        entityType: getEntityTypeFromEntityKind(node.entityKind),
      };
    case 'TestamentaryEntity':
      return { kind: 'subtrust', testamentaryEntityId: node.id };
    case 'ClientProfile':
      return { kind: 'individual', clientProfileId: node.id };
    case 'ClientOrganization':
      return { kind: 'organization', organizationId: node.id };
    default:
      return null;
  }
}

/**
 * The entity-diagram `tile` slot in design mode: resolves the editable record
 * behind each tile and hands off to the shared {@link PresentationEditableEntityTile}
 * for the hover + edit toolbar. Mirrors the estate-waterfall tile.
 */
export function PresentationAwareEntityDiagramTile(props: TileNodeProps) {
  const { state } = useEntityDiagramContext();
  const target = resolveNodeTarget(state.entityDiagramGraph, props.id);

  return <PresentationEditableEntityTile tileProps={props} target={target} />;
}
